import React from 'react';
import Icon from '../../../components/AppIcon';

const AuditReadinessScore = ({ data }) => {
  const getScoreColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 75) return 'text-orange-500';
    return 'text-destructive';
  };

  const getScoreBgColor = (score) => {
    if (score >= 90) return 'bg-success';
    if (score >= 75) return 'bg-orange-500';
    return 'bg-destructive';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Audit Readiness Score</h3>
          <p className="text-sm text-muted-foreground">Overall compliance preparedness assessment</p>
        </div>
        <Icon name="Shield" size={20} color="var(--color-muted-foreground)" />
      </div>

      {/* Overall Score */}
      <div className="text-center mb-8">
        <div className="relative inline-flex items-center justify-center w-32 h-32 mb-4">
          <svg className="transform -rotate-90 w-32 h-32">
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              className="text-muted"
            />
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 56}`}
              strokeDashoffset={`${2 * Math.PI * 56 * (1 - data?.overallScore / 100)}`}
              className={getScoreColor(data?.overallScore)}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute">
            <p className={`text-3xl font-bold ${getScoreColor(data?.overallScore)}`}>
              {data?.overallScore}%
            </p>
          </div>
        </div>
        <p className="text-sm font-medium text-muted-foreground">Ready for Regulatory Audit</p>
      </div>

      {/* Component Scores */}
      <div className="space-y-4 mb-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Documentation Complete</span>
            <span className={`text-sm font-semibold ${getScoreColor(data?.documentationComplete)}`}>
              {data?.documentationComplete}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className={`h-2 rounded-full ${getScoreBgColor(data?.documentationComplete)}`}
              style={{ width: `${data?.documentationComplete}%` }}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Training Up-to-Date</span>
            <span className={`text-sm font-semibold ${getScoreColor(data?.trainingUpToDate)}`}>
              {data?.trainingUpToDate}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className={`h-2 rounded-full ${getScoreBgColor(data?.trainingUpToDate)}`}
              style={{ width: `${data?.trainingUpToDate}%` }}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Equipment Certified</span>
            <span className={`text-sm font-semibold ${getScoreColor(data?.equipmentCertified)}`}>
              {data?.equipmentCertified}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className={`h-2 rounded-full ${getScoreBgColor(data?.equipmentCertified)}`}
              style={{ width: `${data?.equipmentCertified}%` }}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Procedures Updated</span>
            <span className={`text-sm font-semibold ${getScoreColor(data?.proceduresUpdated)}`}>
              {data?.proceduresUpdated}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className={`h-2 rounded-full ${getScoreBgColor(data?.proceduresUpdated)}`}
              style={{ width: `${data?.proceduresUpdated}%` }}
            />
          </div>
        </div>
      </div>

      {/* Gap Analysis */}
      <div className="pt-4 border-t border-border">
        <h4 className="text-sm font-semibold text-foreground mb-3">Identified Gaps</h4>
        <div className="space-y-2">
          {data?.gapAnalysis?.map((gap, index) => (
            <div key={index} className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{gap?.category}</span>
              <span className="font-medium text-foreground">{gap?.gaps} gaps</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AuditReadinessScore;